import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  useFilter,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useCallback, useState } from "react";
import { SearchBox } from "src/components";
import { CostClassificationType, CostSubClassificationType } from "src/generated/graphqlTypes";
import { queryResult } from "src/utils";
import { CostCategoryModal_CostCodeFragment, useCostCategoryModalQuery } from "./CostCategoryModal.generated";

type CategoryId = CostSubClassificationType | CostClassificationType.Soft;
type Category = { id: CategoryId; name: string; description: string[] };
type CategoryFilter = { category?: CategoryId[] };
type Row =
  | { kind: "group"; id: CategoryId; data: Category }
  | { kind: "code"; id: string; data: CostCategoryModal_CostCodeFragment };

/** Opens the definitions from a cost column's info icon. */
export function useOpenCostCategoryModal(): VoidFunction {
  const { openModal } = useModal();
  return useCallback(() => openModal({ content: <CostCategoryModal />, size: "xl" }), [openModal]);
}
export function CostCategoryModal() {
  const { closeModal } = useModal();
  const tid = useTestIds({}, "costCategory");
  const [search, setSearch] = useState<string>();
  const { filter, setFilter } = useFilter({ filterDefs });
  const query = useCostCategoryModalQuery({ variables: { filter: { version: [2] } } });
  return (
    <>
      <ModalHeader>Cost Category</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.pb3.$} {...tid.definitions}>
          {definitions.map(({ name, description }) => (
            <div key={name} css={Css.df.fdc.gapPx(4).$}>
              <div css={Css.smSb.$}>{name}</div>
              {description.map((line) => (
                <div key={line} css={Css.xs.$}>
                  {line}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div css={Css.df.aic.gap1.pb2.$}>
          <SearchBox onSearch={setSearch} clearable updateQueryString={false} />
          <Filters filter={filter} filterDefs={filterDefs} onChange={setFilter} />
        </div>
        {queryResult(query, ({ costCodes }) => (
          <GridTable
            columns={columns}
            rows={createRows(costCodes, filter.category)}
            filter={search}
            fallbackMessage="No cost codes found"
          />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button label="Done" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}

const categories: Category[] = [
  {
    id: CostSubClassificationType.Indirect,
    name: "Indirect Hard Costs",
    description: [
      "Costs for physical work and materials on the site, but outside the structure itself.",
      "Still tangible, on-site construction costs — just not part of the building's core structure.",
    ],
  },
  {
    id: CostSubClassificationType.Direct,
    name: "Direct Hard Costs",
    description: [
      "Costs for the materials and labor that go directly into building the structure itself.",
      "These are the physical, on-structure costs — the ones that scale with the size and complexity of what's being built.",
    ],
  },
  {
    id: CostClassificationType.Soft,
    name: "Soft Costs",
    description: ["Non-physical costs needed to plan, approve, finance, or manage a project, rather than to build it."],
  },
];

const definitions = [
  ...categories,
  {
    name: "Provisional Estimates",
    description: [
      "A placeholder cost added to a house's budget before a trade partner has been awarded for that scope of work. Because it's an estimate rather than an actual bid, the cost codes underneath it can't begin real work until a trade is awarded.",
    ],
  },
];

const filterDefs: FilterDefs<CategoryFilter> = {
  category: multiFilter({
    label: "Category",
    options: categories,
    getOptionValue: (c) => c.id,
    getOptionLabel: (c) => c.name,
  }),
};

const columns: GridColumn<Row>[] = [
  collapseColumn<Row>(),
  column<Row>({
    group: ({ name }) => ({ content: name, colspan: 2, css: Css.smSb.$ }),
    code: ({ number }) => number,
    w: "105px",
  }),
  column<Row>({ group: emptyCell, code: ({ name }) => name }),
];

function createRows(costCodes: CostCategoryModal_CostCodeFragment[], selected?: CategoryId[]): GridDataRow<Row>[] {
  return categories
    .filter((c) => selected?.includes(c.id) ?? true)
    .map((c) => ({
      kind: "group" as const,
      id: c.id,
      data: c,
      children: costCodes
        .filter((cc) => cc.costClassification.code === c.id || cc.costSubClassification?.code === c.id)
        .map((cc) => ({ kind: "code" as const, id: cc.id, data: cc })),
    }))
    .filter((group) => group.children.nonEmpty);
}
